<div
  (mouseenter)="onSidenavMouseEnter()"
  (mouseleave)="onSidenavMouseLeave()"
  (focusin)="onSidenavFocusChange(true)"
  (focusout)="onSidenavFocusChange(false)"
  [class.collapsed]="collapsed()"
  [class.open]="collapsed() && (collapsedOpen$ | async)"
  [class.pinned]="pinned$ | async"
  [class.service-provider-theme]="isServiceProviderWorkspace"
  class="vex-sidenav flex flex-col">
  <div class="vex-sidenav-toolbar__container">
    <div
      class="sidenav-toolbar flex-none flex items-center cursor-pointer"
      (click)="resetNavigation()"
      (keydown.enter)="resetNavigation()"
      tabindex="0">
      <img
        [src]="imageUrl$ | async"
        [alt]="'shared.sidenav.logoAlt' | translate"
        class="w-8 h-8 object-contain select-none flex-none" />
      <h2 class="vex-sidenav-toolbar__headline brand-wordmark flex-auto">
        Proporio
      </h2>

      <!-- L'épingle fige le menu déplié et rend sa place au contenu. Elle
           existe partout où le rail existe, épinglée ou non : la gater sur
           l'état épinglé la faisait disparaître au rechargement d'une session
           épinglée, sans plus aucun moyen de dépingler. Au repos le CSS la
           masque avec les labels, le survol la ramène.
           stopPropagation parce que la barre entière est cliquable. -->
      @if (railCapable$ | async) {
        @let estEpingle = !!(pinned$ | async);
        @let libelleEpingle =
          estEpingle ? 'shared.sidenav.unpin' : 'shared.sidenav.pin';
        <button
          (click)="togglePin(); $event.stopPropagation()"
          [attr.aria-label]="libelleEpingle | translate"
          [attr.aria-pressed]="estEpingle"
          [matTooltip]="libelleEpingle | translate"
          class="vex-sidenav-toolbar__pin flex-none"
          mat-icon-button
          type="button">
          <mat-icon
            class="vex-sidenav-toolbar__pin-icon"
            svgIcon="mat:push_pin"></mat-icon>
        </button>
      }
    </div>

    @if (searchVisible$ | async) {
      <div class="vex-sidenav-search__container">
        <div
          (click)="openSearch()"
          (keydown.enter)="openSearch()"
          class="vex-sidenav-search relative"
          matRipple
          matRippleColor="var(--vex-sidenav-item-ripple-color)"
          tabindex="0">
          <mat-icon
            class="flex-none vex-sidenav-search__icon"
            svgIcon="mat:search"></mat-icon>
          <div class="flex-auto vex-sidenav-search__placeholder">
            {{ "shared.sidenav.quickSearch" | translate }}
          </div>
        </div>
      </div>
    }

    <div class="vex-sidenav-toolbar__divider"></div>
  </div>

  <!-- Le panneau contextuel vit HORS de la zone qui défile : placé dans
       `vex-scrollbar`, il avait pour bloc conteneur la zone de défilement de
       SimpleBar et glissait avec elle. Ici il reste collé, défile dans sa
       propre liste, et le menu principal garde sa position au retour. -->
  <div class="vex-sidenav-body flex-1 relative overflow-hidden">
    <!-- Pas d'`overflow-hidden` sur l'hôte : SimpleBar lit l'overflow de son
         élément et, à `hidden`, coupe le défilement vertical — le menu
         débordait sous le bloc utilisateur sans pouvoir défiler. -->
    <vex-scrollbar class="h-full">
      <!-- Main Navigation -->
      <div
        class="vex-main-navigation pt-6"
        [class.slide-out-left]="showContextual$ | async">
        @for (item of items$ | async; track trackByRoute($index, item)) {
          <vex-sidenav-item [item]="item" [level]="0"></vex-sidenav-item>
        }
      </div>
    </vex-scrollbar>

    <!-- Contextual Navigation Section -->
    <div
      class="vex-contextual-section absolute inset-0 pt-6"
      [class.slide-in-right]="showContextual$ | async"
      [class.slide-out-right]="(showContextual$ | async) === null">
      <!-- Back Navigation -->
      <div class="vex-contextual-back">
        <a
          class="vex-contextual-back-item"
          (click)="onBackClick()"
          (keydown.enter)="onBackClick()"
          tabindex="0"
          matRipple
          matRippleColor="var(--vex-sidenav-item-ripple-color)">
          <mat-icon
            class="vex-contextual-back-item__icon"
            svgIcon="mat:arrow_back"></mat-icon>
          <span class="vex-contextual-back-item__label">{{
            "shared.contextual.backToMain" | translate
          }}</span>
        </a>
      </div>

      <!-- Entity Header -->
      @if (contextualContext$ | async; as context) {
        <div
          class="vex-contextual-header"
          [ngClass]="
            'entity-theme-' + ((contextualContext$ | async)?.type || 'default')
          ">
          <div class="entity-info">
            <div class="entity-avatar">
              <mat-icon
                [svgIcon]="getEntityIcon(context.type)"
                class="entity-icon"></mat-icon>
            </div>
            <div class="entity-details">
              <h3 class="entity-name">{{ context.name | translate }}</h3>
              @if (context.subtitle) {
                <p class="entity-subtitle">
                  {{ context.subtitle | translate: context.subtitleParams }}
                </p>
              }
            </div>
          </div>
        </div>
      }

      <!-- Contextual Navigation Items -->
      <nav class="vex-contextual-nav flex-1">
        @for (
          item of contextualItems$ | async;
          track trackByContextualRoute($index, item)
        ) {
          <!-- Separator -->
          @if (item.id.startsWith("separator")) {
            <div class="vex-contextual-nav-separator"></div>
          }
          <!-- Group header (non-clickable section label) -->
          @if (item.id.startsWith("group-")) {
            <div class="vex-contextual-nav-header">
              {{ item.label | translate }}
            </div>
          }
          <!-- Parent Item with Children -->
          @if (
            !item.id.startsWith("separator") &&
            !item.id.startsWith("group-") &&
            item.children &&
            item.children.length > 0
          ) {
            <div class="vex-contextual-nav-group">
              <!-- Parent Item -->
              <a
                (click)="toggleContextualExpanded(item.id)"
                (keydown.enter)="toggleContextualExpanded(item.id)"
                tabindex="0"
                class="vex-contextual-nav-item vex-contextual-nav-item--parent"
                [class.vex-contextual-nav-item--expanded]="
                  isContextualExpanded(item.id)
                "
                matRipple
                matRippleColor="var(--vex-sidenav-item-ripple-color)">
                <mat-icon
                  [svgIcon]="'mat:' + item.icon"
                  class="vex-contextual-nav-item__icon"></mat-icon>
                <span class="vex-contextual-nav-item__label">{{
                  item.label | translate
                }}</span>
                <mat-icon
                  class="vex-contextual-nav-item__expand-icon"
                  [class.vex-contextual-nav-item__expand-icon--expanded]="
                    isContextualExpanded(item.id)
                  ">
                  expand_more
                </mat-icon>
              </a>
              <!-- Children Items -->
              <div
                class="vex-contextual-nav-children"
                [class.vex-contextual-nav-children--expanded]="
                  isContextualExpanded(item.id)
                ">
                @for (child of item.children; track child) {
                  <a
                    [routerLink]="child.clickHandler ? null : child.route"
                    [href]="child.clickHandler ? 'javascript:void(0)' : null"
                    (click)="
                      child.clickHandler ? onContextualItemClick(child) : null
                    "
                    (keydown.enter)="
                      child.clickHandler ? onContextualItemClick(child) : null
                    "
                    routerLinkActive="vex-contextual-nav-item--active"
                    class="vex-contextual-nav-item vex-contextual-nav-item--child"
                    [class.vex-contextual-nav-item--active]="isContextualItemActive(child)"
                    [class.vex-contextual-nav-item--disabled]="child.disabled"
                    matRipple
                    matRippleColor="var(--vex-sidenav-item-ripple-color)">
                    <mat-icon
                      [svgIcon]="'mat:' + child.icon"
                      class="vex-contextual-nav-item__icon"></mat-icon>
                    <span class="vex-contextual-nav-item__label">{{
                      child.label | translate
                    }}</span>
                    @if (child.badge) {
                      <span
                        class="vex-contextual-nav-item__badge"
                        [ngClass]="child.badgeClass || ''">{{
                        child.badge
                      }}</span>
                    }
                  </a>
                }
              </div>
            </div>
          }
          <!-- Regular Navigation Item (no children) -->
          @if (
            !item.id.startsWith("separator") &&
            !item.id.startsWith("group-") &&
            (!item.children || item.children.length === 0)
          ) {
            <a
              [routerLink]="item.clickHandler ? null : item.route"
              [href]="item.clickHandler ? 'javascript:void(0)' : null"
              (click)="item.clickHandler ? onContextualItemClick(item) : null"
              (keydown.enter)="
                item.clickHandler ? onContextualItemClick(item) : null
              "
              routerLinkActive="vex-contextual-nav-item--active"
              class="vex-contextual-nav-item"
              [class.vex-contextual-nav-item--active]="isContextualItemActive(item)"
              [class.vex-contextual-nav-item--disabled]="item.disabled"
              matRipple
              matRippleColor="var(--vex-sidenav-item-ripple-color)">
              <mat-icon
                [svgIcon]="'mat:' + item.icon"
                class="vex-contextual-nav-item__icon"></mat-icon>
              <span class="vex-contextual-nav-item__label">{{
                item.label | translate
              }}</span>
              @if (item.badge) {
                <span
                  class="vex-contextual-nav-item__badge"
                  [ngClass]="item.badgeClass || ''">{{
                  item.badge
                }}</span>
              }
            </a>
          }
        }
      </nav>
    </div>

    <!-- Redundant footer logo removed for cleaner design -->
  </div>

  @if (userVisible$ | async) {
    <div class="vex-sidenav-user__container flex-none">
      <div
        #userProfileMenuOriginRef
        (click)="openProfileMenu(userProfileMenuOriginRef)"
        (keydown.enter)="openProfileMenu(userProfileMenuOriginRef)"
        [class.vex-sidenav-user--open]="userMenuOpen$ | async"
        class="vex-sidenav-user flex items-center"
        matRipple
        matRippleColor="var(--vex-sidenav-item-ripple-color)"
        tabindex="0">
        @if (user()?.photoURL) {
          <div class="vex-sidenav-user__image flex-none">
            <img
              [src]="$safeNavigationMigration(user()?.photoURL)"
              [alt]="'shared.sidenav.userAvatarAlt' | translate"
              class="w-full h-full object-cover" />
          </div>
        } @else {
          <div
            class="vex-sidenav-user__image flex-none flex items-center justify-center bg-white/10 text-white/70">
            <mat-icon svgIcon="mat:person"></mat-icon>
          </div>
        }
        <div class="vex-sidenav-user__content flex-auto">
          <div class="vex-sidenav-user__title">{{ userName() }}</div>
          <div class="vex-sidenav-user__subtitle flex items-center gap-1">
            @if (!isPersonalWorkspace) {
              <mat-icon
                class="workspace-icon"
                svgIcon="mat:business"></mat-icon>
            }
            <span>{{ workspaceName }}</span>
          </div>
        </div>
        <mat-icon
          class="vex-sidenav-user__dropdown-icon flex-none"
          svgIcon="mat:arrow_drop_down"></mat-icon>
      </div>
    </div>
  }
</div>
